// client/src/pages/WorkOrdersList.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Button,
  TextField, MenuItem, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Snackbar, Select, FormControl, InputLabel,
  Tab, Tabs, List, ListItem, ListItemText, ListItemAvatar, Avatar,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  History as HistoryIcon, CheckCircle, Pending, Warning,
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import axios from '../api/axiosConfig';

const fetchWorkOrders = async ({ queryKey }) => {
  const [, filters] = queryKey;
  const params = {};
  if (filters.status) params.status = filters.status;
  if (filters.clientId) params.clientId = filters.clientId;
  if (filters.mechanicId) params.mechanicId = filters.mechanicId;
  const { data } = await axios.get('/workorders', { params });
  return data;
};

const fetchClients = async () => {
  const { data } = await axios.get('/clients');
  return data;
};

const fetchMechanics = async () => {
  const { data } = await axios.get('/users?role=mechanic');
  return data;
};

const deleteWorkOrder = async (id) => axios.delete(`/workorders/${id}`);
const changeStatus = async ({ id, status, notes }) =>
  axios.patch(`/workorders/${id}/status`, { status, notes });

const WorkOrdersList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: '', clientId: '', mechanicId: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, id: null, currentStatus: '' });
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [historyDialog, setHistoryDialog] = useState({ open: false, workOrder: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: workOrders, isLoading, error } = useQuery({
    queryKey: ['workorders', filters],
    queryFn: fetchWorkOrders,
  });
  const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const { data: mechanics } = useQuery({ queryKey: ['mechanics'], queryFn: fetchMechanics });

  const deleteMutation = useMutation({
    mutationFn: deleteWorkOrder,
    onSuccess: () => {
      queryClient.invalidateQueries(['workorders']);
      setSnackbar({ open: true, message: 'Work order deleted', severity: 'success' });
      setDeleteDialog({ open: false, id: null });
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Delete failed', severity: 'error' }),
  });

  const statusMutation = useMutation({
    mutationFn: changeStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(['workorders']);
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      setStatusDialog({ open: false, id: null, currentStatus: '' });
      setNewStatus('');
      setStatusNotes('');
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Update failed', severity: 'error' }),
  });

  const getStatusColor = (status) => {
    const colors = { pending: 'warning', 'in-progress': 'info', completed: 'success', cancelled: 'error' };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle />;
    if (status === 'pending') return <Pending />;
    return <Warning />;
  };

  const handleStatusChange = (id, currentStatus) => {
    setNewStatus(currentStatus);
    setStatusDialog({ open: true, id, currentStatus });
  };

  const handleSaveStatus = () => {
    if (!newStatus) return;
    statusMutation.mutate({ id: statusDialog.id, status: newStatus, notes: statusNotes });
  };

  const handleViewHistory = (workOrder) => {
    setHistoryDialog({ open: true, workOrder });
  };

  if (isLoading) return <Layout><Typography>Loading...</Typography></Layout>;
  if (error) return <Layout><Typography color="error">Error: {error.message}</Typography></Layout>;

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight={600}>Work Orders</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/workorder/new')}>New Work Order</Button>
        </Box>

        {/* Фильтры */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Status" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Client" value={filters.clientId} onChange={(e) => setFilters({...filters, clientId: e.target.value})}>
                <MenuItem value="">All Clients</MenuItem>
                {clients?.map(client => <MenuItem key={client._id} value={client._id}>{client.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Mechanic" value={filters.mechanicId} onChange={(e) => setFilters({...filters, mechanicId: e.target.value})}>
                <MenuItem value="">All Mechanics</MenuItem>
                {mechanics?.map(mech => <MenuItem key={mech._id} value={mech._id}>{mech.fullName}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Таблица */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell><TableCell>Client</TableCell><TableCell>Vehicle</TableCell>
                <TableCell>Mechanic</TableCell><TableCell>Start Time</TableCell><TableCell>End Time</TableCell>
                <TableCell>Status</TableCell><TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workOrders?.map(wo => (
                <TableRow key={wo._id}>
                  <TableCell>{wo.title}</TableCell>
                  <TableCell>{wo.clientId?.name || '-'}</TableCell>
                  <TableCell>{wo.vehicleId?.licensePlate || '-'}</TableCell>
                  <TableCell>{wo.mechanicId?.fullName || 'Unassigned'}</TableCell>
                  <TableCell>{new Date(wo.startTime).toLocaleString()}</TableCell>
                  <TableCell>{new Date(wo.endTime).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={wo.status}
                      color={getStatusColor(wo.status)}
                      size="small"
                      onClick={() => handleStatusChange(wo._id, wo.status)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => navigate(`/workorder/edit/${wo._id}`)}><EditIcon /></IconButton>
                    <IconButton onClick={() => setDeleteDialog({ open: true, id: wo._id })}><DeleteIcon /></IconButton>
                    <IconButton onClick={() => handleViewHistory(wo)}><HistoryIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Диалог смены статуса */}
        <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, id: null, currentStatus: '' })}>
          <DialogTitle>Change Status</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="New Status">
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes (optional)"
              multiline
              rows={3}
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialog({ open: false, id: null, currentStatus: '' })}>Cancel</Button>
            <Button onClick={handleSaveStatus} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог истории изменений */}
        <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, workOrder: null })} maxWidth="md" fullWidth>
          <DialogTitle>Status History</DialogTitle>
          <DialogContent>
            {historyDialog.workOrder?.statusHistory?.length > 0 ? (
              <List>
                {historyDialog.workOrder.statusHistory.map((entry, idx) => (
                  <ListItem key={idx} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getStatusColor(entry.status) }}>
                        {getStatusIcon(entry.status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`Status changed to ${entry.status}`}
                      secondary={
                        <>
                          <Typography variant="caption" display="block">
                            Changed by: {entry.changedBy?.fullName || 'Unknown'} at {new Date(entry.changedAt).toLocaleString()}
                          </Typography>
                          {entry.notes && <Typography variant="caption" display="block">Notes: {entry.notes}</Typography>}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No status changes recorded.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialog({ open: false, workOrder: null })}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог удаления */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>Are you sure you want to delete this work order?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
            <Button onClick={() => deleteMutation.mutate(deleteDialog.id)} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({...snackbar, open: false})}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default WorkOrdersList;